import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Sparkles } from 'lucide-react';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { formatCurrency } from '@/lib/currency';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { optimizeImageUrl } from '@/utils/image-optimizer';

interface NewArrivalsProps {
  language: string;
}

/**
 * "New Arrivals" section for the city home — same product-card shape as
 * FlowerAuraProducts but sorted by `newest` and capped at 8 cards.
 *
 * Why we added this: every card name is an <h3>, so 8 fresh cards adds
 * ~8 more H3 entries to the prerendered HTML. Combined with the section's
 * own <h2>, this closes most of the H2/H3 gap vs Floward's Alexandria
 * page (Floward had 39 H3s, we had 12). Also signals freshness to Google
 * — pages with "recently added" sections tend to get recrawled more often.
 */
const NewArrivals = ({ language }: NewArrivalsProps) => {
  const { selectedCity } = useDeliveryLocation();
  const { data: products, isLoading } = useProducts({
    sortBy: 'newest',
    city: selectedCity?.slug,
  });
  const { addItem } = useCart();
  const { navigateWithCity } = useCityNavigation();

  const t = language === 'ar'
    ? { title: 'وصل حديثاً', subtitle: 'أحدث الإضافات لمتجرنا', viewAll: 'عرض الكل' }
    : { title: 'New Arrivals', subtitle: 'Freshly added to our shop', viewAll: 'View All' };

  const handleAdd = (product: any, e: React.MouseEvent) => {
    e.stopPropagation();
    addItem({
      id: product.id,
      name: language === 'ar' ? product.name_ar : product.name_en,
      price: product.price,
      image_url: product.image_url,
    });
  };

  const displayed = (products || []).slice(0, 8);

  // Skeleton matches the live grid so layout doesn't jump on hydration.
  if (isLoading) {
    return (
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" /> {t.title}
            </h2>
            <p className="text-muted-foreground">{t.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-square bg-muted animate-pulse" />
                <CardContent className="p-4 space-y-2">
                  <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-muted rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (displayed.length === 0) return null;

  return (
    <section className="py-16 bg-muted/20">
      <div className="container mx-auto px-4">
        <div className={`text-center mb-10 ${language === 'ar' ? 'text-right sm:text-center' : ''}`}>
          <h2 className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" /> {t.title}
          </h2>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {displayed.map((product: any) => (
            <Card
              key={product.id}
              className="overflow-hidden cursor-pointer group hover:shadow-lg transition-shadow"
              onClick={() => navigateWithCity(`/products/${product.slug}`)}
            >
              <div className="relative aspect-square overflow-hidden bg-muted">
                {product.image_url && (
                  <img
                    src={optimizeImageUrl(product.image_url, 400)}
                    alt={language === 'ar' ? product.name_ar : product.name_en}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                  aria-label="Save"
                >
                  <Heart className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
                </button>
                <span
                  className={`absolute top-3 ${language === 'ar' ? 'right-14' : 'left-3'} bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded`}
                  aria-hidden="true"
                >
                  {language === 'ar' ? 'جديد' : 'NEW'}
                </span>
              </div>
              <CardContent className="p-4">
                {/* H3 — counted by SEO crawlers as a heading anchor, gives the
                    product name structural weight on top of being a link. */}
                <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {language === 'ar' ? product.name_ar : product.name_en}
                </h3>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-lg font-bold text-foreground">
                    {formatCurrency(product.price, { language: language as 'ar' | 'en' })}
                  </div>
                  {product.original_price && Number(product.original_price) > Number(product.price) && (
                    <div className="text-sm text-muted-foreground line-through">
                      {formatCurrency(product.original_price, { language: language as 'ar' | 'en' })}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  className="w-full"
                  size="sm"
                  onClick={(e) => handleAdd(product, e)}
                >
                  {language === 'ar' ? 'أضف للسلة' : 'Add to Cart'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigateWithCity('/products?sort=newest')}
          >
            {t.viewAll}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NewArrivals;
